import { useMemo, useState } from "react";
import type { ProjectManifest } from "@pix/shared";

export interface Snippet {
  id: string;
  label: string;
  category: SnippetCategory;
  description?: string;
  /** \n 区切りの挿入テキスト。末尾に空行を入れない(挿入側で揃える) */
  body: string;
}

export type SnippetCategory = "基本" | "キャラ" | "演出" | "音" | "制御" | "変数";

const STATIC: Snippet[] = [
  // 基本
  { id: "msg", label: "メッセージ", category: "基本", body: "アリス: ここにセリフ" },
  { id: "narration", label: "ナレーション", category: "基本", body: "> ここにナレーション" },
  {
    id: "msg-block",
    label: "発話 2 行",
    category: "基本",
    body: "アリス: 1 行目\n2 行目",
  },

  // キャラ
  {
    id: "chara-enter",
    label: "登場 (center)",
    category: "キャラ",
    body: "@chara show id=alice pos=center face=normal",
  },
  {
    id: "chara-leave",
    label: "退場",
    category: "キャラ",
    body: "@chara hide id=alice",
  },
  {
    id: "chara-face",
    label: "表情変更",
    category: "キャラ",
    body: "@chara face id=alice expression=smile",
  },
  {
    id: "chara-2",
    label: "2 人登場",
    category: "キャラ",
    body:
      "@chara show id=alice pos=left face=normal\n@chara show id=bob pos=right face=normal",
  },
  {
    id: "chara-hideall",
    label: "全員退場",
    category: "キャラ",
    body: "@chara hide_all",
  },

  // 演出
  {
    id: "wait",
    label: "wait 500ms",
    category: "演出",
    body: "@wait ms=500",
  },
  {
    id: "transition-fade",
    label: "フェード遷移",
    category: "演出",
    body: "@transition effect=fade ms=600",
  },
  {
    id: "effect-shake",
    label: "画面シェイク",
    category: "演出",
    body: "@effect shake intensity=4 ms=400",
  },
  {
    id: "screen-flash",
    label: "画面フラッシュ",
    category: "演出",
    body: "@screen flash color=#ffffff ms=200",
  },

  // 音
  {
    id: "bgm-play",
    label: "BGM 再生",
    category: "音",
    body: "@bgm play file=intro vol=80",
  },
  {
    id: "bgm-stop",
    label: "BGM 停止",
    category: "音",
    body: "@bgm stop",
  },
  {
    id: "se-once",
    label: "SE 単発",
    category: "音",
    body: "@se play slot=0 file=click",
  },

  // 制御
  {
    id: "label",
    label: "ラベル",
    category: "制御",
    body: "## new_label",
  },
  {
    id: "jump",
    label: "ジャンプ",
    category: "制御",
    body: "@jump #next_label",
  },
  {
    id: "call",
    label: "コール + リターン",
    category: "制御",
    body: "@call #sub_routine",
  },
  {
    id: "end",
    label: "終了",
    category: "制御",
    body: "@end",
  },
  {
    id: "choice2",
    label: "選択肢 (2)",
    category: "制御",
    body: "- [はい](#yes_label)\n- [いいえ](#no_label)",
  },
  {
    id: "choice3",
    label: "選択肢 (3)",
    category: "制御",
    body:
      "- [選択 A](#a_label)\n- [選択 B](#b_label)\n- [選択 C](#c_label)",
  },
  {
    id: "if-else",
    label: "if / else",
    category: "制御",
    body:
      "@if local.flag\n  > 条件が真のとき\n@else\n  > 条件が偽のとき\n@endif",
  },
  {
    id: "for",
    label: "@for ループ",
    category: "制御",
    body: "@for i = 0 to 3\n  > {i} 回目\n@endfor",
  },

  // 変数
  {
    id: "set-inc",
    label: "@set 加算",
    category: "変数",
    body: "@set local.affection += 1",
  },
  {
    id: "set-flag",
    label: "@set フラグ",
    category: "変数",
    body: "@set global.first_visit = true",
  },
  {
    id: "unset",
    label: "@unset 解除",
    category: "変数",
    body: "@unset local.tmp",
  },
];

const CATEGORIES: SnippetCategory[] = ["基本", "キャラ", "演出", "音", "制御", "変数"];

interface Props {
  manifest: ProjectManifest;
  onInsert: (text: string) => void;
}

export function SnippetsPanel({ manifest, onInsert }: Props) {
  const [query, setQuery] = useState("");

  /** プロジェクト固有スニペット: manifest からキャラ別の登場テンプレを自動生成 */
  const dynamic = useMemo<Snippet[]>(() => {
    const chars = Object.entries(manifest.characters ?? {});
    return chars.slice(0, 6).map(([id, def]) => ({
      id: `chara-${id}`,
      label: `${def.name} 登場`,
      category: "キャラ" as const,
      description: `id=${id}`,
      body: `@chara show id=${id} pos=center face=${def.expressions?.[0] ?? "normal"}`,
    }));
  }, [manifest.characters]);

  const all = useMemo<Snippet[]>(() => [...dynamic, ...STATIC], [dynamic]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return all;
    return all.filter(
      (s) =>
        s.label.toLowerCase().includes(q) ||
        s.body.toLowerCase().includes(q) ||
        s.category.toLowerCase().includes(q),
    );
  }, [all, query]);

  return (
    <div className="snip-panel">
      <div className="snip-search">
        <input
          type="text"
          placeholder="検索"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>
      <div className="snip-list">
        {CATEGORIES.map((cat) => {
          const list = filtered.filter((s) => s.category === cat);
          if (list.length === 0) return null;
          return (
            <div key={cat} className="snip-group">
              <div className="snip-group-title">{cat}</div>
              {list.map((s) => (
                <button
                  key={s.id}
                  className="snip-item"
                  onClick={() => onInsert(s.body)}
                  title={s.body}
                >
                  <span className="snip-label">{s.label}</span>
                  {s.description && (
                    <span className="snip-desc">{s.description}</span>
                  )}
                </button>
              ))}
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="snip-empty">該当するスニペットがありません</div>
        )}
      </div>
    </div>
  );
}
